import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from '../TradeFlowTrackingTab.module.css';
import { getSymbolJourney, TradeJourney } from '../../../services/tradeFlowAdminApi';

interface JourneySectionProps {
  dateFrom: string;
  dateTo: string;
}

const STAGE_COLORS: Record<string, string> = {
  platform_fetch: '#4fc3f7',
  genesis: '#81c784',
  pipeline: '#ffb74d',
  xgboost: '#ba68c8',
  scoring: '#e57373',
  smart_pool: '#4db6ac',
  active_pool: '#ff8a65',
  live_gate: '#9575cd',
  technical: '#90a4ae',
  sniper: '#ffd54f',
  execution: '#4db6ac',
};

const JourneySection: React.FC<JourneySectionProps> = ({ dateFrom, dateTo }) => {
  const [searchSymbol, setSearchSymbol] = useState('');
  const [triggerSymbol, setTriggerSymbol] = useState('');

  const {
    data: result,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['admin', 'trade-flow', 'journey', triggerSymbol, dateFrom, dateTo],
    queryFn: () =>
      getSymbolJourney(triggerSymbol, {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit: 20,
      }),
    enabled: triggerSymbol.length > 0,
  });

  const journeys = result?.data ?? [];
  const validationError =
    !triggerSymbol && !isLoading
      ? null
      : null;

  const handleSearch = () => {
    const symbol = searchSymbol.trim().toUpperCase();
    if (!symbol) return;
    setTriggerSymbol(symbol);
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>Trade Journey</h2>
        <span className={styles.sectionBadge}>Tab 12</span>
      </div>
      <p className={styles.sectionDescription}>
        Enter a trading pair symbol to see its complete journey through all 10 stages.
        Each row shows one recommendation ID's path from platform fetch to execution (or rejection).
      </p>

      <div className={styles.journeySearch}>
        <input
          type="text"
          placeholder="Symbol (e.g., BTC/USDT)..."
          value={searchSymbol}
          onChange={(e) => setSearchSymbol(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          className={styles.filterInput}
        />
        <button onClick={handleSearch} disabled={isLoading} className={styles.searchButton}>
          {isLoading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {isError && <div className={styles.errorBanner}>{(error as Error)?.message || 'Failed to fetch journey'}</div>}

      {triggerSymbol && !isLoading && journeys.length === 0 && !isError && (
        <div className={styles.emptyState}>
          No journey data found for symbol "{triggerSymbol}" in the selected date range.
        </div>
      )}

      {journeys.map((journey) => (
        <div key={journey.recommendation_id} className={styles.journeyCard}>
          <div className={styles.journeyHeader}>
            <span className={styles.mono}>{journey.recommendation_id}</span>
            <span className={styles.journeySymbol}>{journey.symbol}</span>
          </div>

          <div className={styles.timeline}>
            {journey.stages.map((stage) => {
              const color = STAGE_COLORS[stage.stage] || '#666';
              const statusIcon = stage.passed === true ? '\u2713'
                : stage.passed === false ? '\u2717'
                : '\u00B7';

              return (
                <div key={stage.stage} className={styles.timelineStage}>
                  <div
                    className={styles.timelineDot}
                    style={{
                      backgroundColor: stage.passed === true ? color
                        : stage.passed === false ? '#666'
                        : '#333',
                      borderColor: color,
                    }}
                  />
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineLabel}>
                      <span
                        className={styles.timelineStatus}
                        style={{ color }}
                      >
                        {statusIcon}
                      </span>
                      Stage {stage.stage_number}: {stage.stage_label}
                      {stage.score !== null && (
                        <span className={styles.timelineScore}>
                          Score: {stage.score.toFixed(3)}
                        </span>
                      )}
                    </div>
                    {stage.details.length > 0 && (
                      <div className={styles.timelineDetails}>
                        {stage.details.map((detail, di) => (
                          <div key={di} className={styles.timelineDetailRow}>
                            {Object.entries(detail).map(([key, value]) => {
                              if (value === null || value === undefined) return null;
                              const displayValue = typeof value === 'boolean'
                                ? (value ? 'PASS' : 'FAIL')
                                : typeof value === 'number' ? value.toFixed(4) : String(value);
                              return (
                                <span key={key} className={styles.timelineDetailItem}>
                                  <span className={styles.detailKey}>{key}:</span>
                                  <span className={styles.detailValue}>{displayValue}</span>
                                </span>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default JourneySection;
